/**
 * DashboardSettings — Toggle panel for dashboard executive features.
 * Persists preferences to localStorage.
 */

import { useState, useEffect, useCallback } from "react";
import posthog from "posthog-js";

export interface DashboardFeatures {
  scoreboard: boolean;
  sparklines: boolean;
  animatedNumbers: boolean;
  forecast: boolean;
}

const STORAGE_KEY = "wmp-dashboard-features";

const DEFAULT_FEATURES: DashboardFeatures = {
  scoreboard: true,
  sparklines: true,
  animatedNumbers: true,
  forecast: false,
};

/**
 * Hook to manage dashboard feature toggles with localStorage persistence.
 */
export function useDashboardFeatures() {
  const [features, setFeatures] = useState<DashboardFeatures>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_FEATURES, ...JSON.parse(stored) };
      }
    } catch {
      // ignore parse errors
    }
    return DEFAULT_FEATURES;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
    } catch {
      // ignore storage errors
    }
  }, [features]);

  const toggle = useCallback((key: keyof DashboardFeatures) => {
    setFeatures((prev) => {
      const newValue = !prev[key];
      posthog.capture("feature_toggled", { feature: key, enabled: newValue });
      return { ...prev, [key]: newValue };
    });
  }, []);

  const resetAll = useCallback(() => {
    setFeatures(DEFAULT_FEATURES);
  }, []);

  return { features, toggle, resetAll };
}

/**
 * Feature toggle descriptions for the settings panel.
 */
const FEATURE_INFO: Array<{
  key: keyof DashboardFeatures;
  label: string;
  description: string;
}> = [
  {
    key: "scoreboard",
    label: "Portfolio Scoreboard",
    description: "Aggregate metrics across all companies at the top",
  },
  {
    key: "sparklines",
    label: "Trend Sparklines",
    description: "Mini trend charts on each company card",
  },
  {
    key: "animatedNumbers",
    label: "Animated Numbers",
    description: "Numbers count up when the page loads",
  },
  {
    key: "forecast",
    label: "Forecast Cards",
    description: "Full-month / full-year forecast vs budget on each company card",
  },
];

interface DashboardSettingsProps {
  features: DashboardFeatures;
  onToggle: (key: keyof DashboardFeatures) => void;
  onReset: () => void;
}

export function DashboardSettings({
  features,
  onToggle,
  onReset,
}: DashboardSettingsProps) {
  const [open, setOpen] = useState(false);

  const allEnabled = Object.values(features).every(Boolean);
  const someDisabled = Object.values(features).some((v) => !v);

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
          open
            ? "bg-wmp-500 text-white shadow-sm"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"
        }`}
      >
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Customize
        {someDisabled && (
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white rounded-xl shadow-pop border border-gray-200/70 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                Dashboard Features
              </h3>
              {!allEnabled && (
                <button
                  onClick={onReset}
                  className="text-[10px] text-wmp-600 hover:underline font-medium"
                >
                  Enable All
                </button>
              )}
            </div>
            <div className="py-1">
              {FEATURE_INFO.map(({ key, label, description }) => (
                <button
                  key={key}
                  onClick={() => onToggle(key)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  {/* Toggle Switch */}
                  <div
                    className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${
                      features[key] ? "bg-wmp-500" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        features[key] ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
